import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaHome, FaPhone, FaClock, FaShieldAlt, FaRegCheckCircle } from 'react-icons/fa';

const GetEstimate = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'FL',
    zipCode: '',
    projectType: '',
    timeframe: '',
    message: '',
    preferredContact: 'phone',
    bestTime: 'morning'
  });

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add form submission logic here
    console.log('Form submitted:', formData);
  };

  const projectTypes = [
    'Impact Windows',
    'Entry Doors',
    'Sliding Glass Doors',
    'French Doors',
    'Complete Home Package',
    'Other'
  ];

  const timeframes = [
    'Immediately',
    'Within 1 Month',
    'Within 3 Months',
    '3+ Months',
    'Just Researching'
  ];

  const inputClasses = "w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition duration-200";
  const labelClasses = "block text-gray-700 font-medium mb-2";

  return (
    <div className="pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">FREE CONSULTATION</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Get Your Free Estimate Today
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform your home with our premium impact windows and doors. Fill out the form below and receive a customized estimate within 24 hours.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <motion.div
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={fadeIn.transition}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
              {/* Personal Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaHome className="w-6 h-6 text-primary-600 mr-3" />
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className={labelClasses}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={labelClasses}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaPhone className="w-6 h-6 text-primary-600 mr-3" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className={labelClasses}>
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelClasses}>
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaHome className="w-6 h-6 text-primary-600 mr-3" />
                  Property Address
                </h2>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="address" className={labelClasses}>
                      Street Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="col-span-2">
                      <label htmlFor="city" className={labelClasses}>
                        City *
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className={labelClasses}>
                        State
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        disabled
                        className={`${inputClasses} bg-gray-50`}
                      />
                    </div>
                    <div>
                      <label htmlFor="zipCode" className={labelClasses}>
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaShieldAlt className="w-6 h-6 text-primary-600 mr-3" />
                  Project Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="projectType" className={labelClasses}>
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className={inputClasses}
                    >
                      <option value="">Select a project type</option>
                      {projectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeframe" className={labelClasses}>
                      Timeframe
                    </label>
                    <select
                      id="timeframe"
                      name="timeframe"
                      value={formData.timeframe}
                      onChange={handleChange}
                      className={inputClasses}
                    >
                      <option value="">Select a timeframe</option>
                      {timeframes.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaRegCheckCircle className="w-6 h-6 text-primary-600 mr-3" />
                  Additional Information
                </h2>
                <div>
                  <label htmlFor="message" className={labelClasses}>
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className={inputClasses}
                    placeholder="Please provide any additional details about your project..."
                  />
                </div>
              </div>

              {/* Contact Preferences */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaClock className="w-6 h-6 text-primary-600 mr-3" />
                  Contact Preferences
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClasses}>
                      Preferred Contact Method
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="phone"
                          checked={formData.preferredContact === 'phone'}
                          onChange={handleChange}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        Phone
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="email"
                          checked={formData.preferredContact === 'email'}
                          onChange={handleChange}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        Email
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={labelClasses}>
                      Best Time to Contact
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bestTime"
                          value="morning"
                          checked={formData.bestTime === 'morning'}
                          onChange={handleChange}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        Morning (9AM - 12PM)
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bestTime"
                          value="afternoon"
                          checked={formData.bestTime === 'afternoon'}
                          onChange={handleChange}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        Afternoon (12PM - 5PM)
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bestTime"
                          value="evening"
                          checked={formData.bestTime === 'evening'}
                          onChange={handleChange}
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                        />
                        Evening (5PM - 7PM)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-primary-600 text-white py-4 px-6 rounded-md text-lg font-semibold hover:bg-primary-700 transition duration-300 flex items-center justify-center"
              >
                <FaRegCheckCircle className="w-5 h-5 mr-2" />
                Submit Request
              </button>
            </form>
          </motion.div>

          {/* Sidebar Information */}
          <motion.div
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="lg:col-span-1 space-y-8"
          >
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FaRegCheckCircle className="w-6 h-6 text-primary-600 mr-3" />
                What to Expect
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mr-3">1</span>
                  <p className="text-gray-600">
                    Submit your request using this form
                  </p>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mr-3">2</span>
                  <p className="text-gray-600">
                    Receive a call from our expert within 24 hours
                  </p>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mr-3">3</span>
                  <p className="text-gray-600">
                    Schedule a free in-home consultation
                  </p>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mr-3">4</span>
                  <p className="text-gray-600">
                    Get a detailed estimate for your project
                  </p>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FaPhone className="w-6 h-6 text-primary-600 mr-3" />
                Contact Information
              </h2>
              <div className="space-y-4 text-gray-600">
                <p className="flex items-center">
                  <FaPhone className="w-5 h-5 text-primary-600 mr-3" />
                  (754) 250-3987
                </p>
                <p className="flex items-center">
                  <FaClock className="w-5 h-5 text-primary-600 mr-3" />
                  Mon-Fri: 8AM-6PM
                </p>
                <p className="flex items-center">
                  <FaClock className="w-5 h-5 text-primary-600 mr-3" />
                  Saturday: 9AM-4PM
                </p>
                <p className="flex items-center">
                  <FaClock className="w-5 h-5 text-primary-600 mr-3" />
                  Sunday: Closed
                </p>
              </div>
            </div>

            <div className="bg-primary-50 rounded-lg p-8 border-2 border-primary-100">
              <h3 className="text-xl font-bold text-primary-900 mb-4 flex items-center">
                <FaShieldAlt className="w-6 h-6 text-primary-600 mr-3" />
                Quick Response Guarantee
              </h3>
              <p className="text-primary-700">
                We guarantee to respond to your request within 24 hours or you'll
                receive an additional 5% off your project!
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GetEstimate; 